import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import VerificationBadges from '../verification/VerificationBadges';

const ReviewHistory = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    sortBy: 'newest',
    rating: 'all',
    location: ''
  });
  const [stats, setStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    helpfulVotes: 0,
    monthlyReviews: 0
  });

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchReviews();
      fetchStats();
    }
  }, [isOpen, isAuthenticated, filters]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = new URLSearchParams(filters);
      const response = await fetch(`/api/user/reviews?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setReviews(data.data || []);
        } else {
          // API endpoint not implemented yet
          console.log('Review API endpoint not yet implemented');
          setReviews([]);
        }
      } else if (response.status === 404) {
        // Endpoint doesn't exist yet - this is expected during development
        console.log('Review API endpoint not found - using empty data');
        setReviews([]);
      } else {
        try {
          const errorData = await response.json();
          setError(errorData.message || 'Failed to load reviews');
        } catch {
          setError('Failed to load reviews');
        }
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
      // Don't show error for network issues during development
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/user/reviews/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          setStats(data.data || {});
        } else {
          // API endpoint not implemented yet
          console.log('Review stats API endpoint not yet implemented');
          setStats({ total: 0, averageRating: 0, thisMonth: 0 });
        }
      } else if (response.status === 404) {
        // Endpoint doesn't exist yet - this is expected during development
        console.log('Review stats API endpoint not found - using default data');
        setStats({ total: 0, averageRating: 0, thisMonth: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch review stats:', error);
      // Use default stats during development
      setStats({ total: 0, averageRating: 0, thisMonth: 0 });
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`/api/user/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        setReviews(reviews.filter(review => review.id !== reviewId));
        fetchStats();
        alert('Review deleted successfully');
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to delete review');
      }
    } catch (error) {
      console.error('Failed to delete review:', error);
      alert('Failed to delete review');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const StarDisplay = ({ rating, size = 'small' }) => {
    const sizes = {
      small: '14px',
      medium: '18px'
    };

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{
              fontSize: sizes[size],
              color: rating >= star ? '#FFD700' : '#ddd'
            }}
          >
            ★
          </span>
        ))}
        <span style={{ 
          marginLeft: '6px', 
          fontSize: '12px',
          color: '#666'
        }}>
          {rating.toFixed(1)}
        </span>
      </div>
    );
  };

  const ReviewCard = ({ review }) => (
    <div style={{
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '12px'
      }}>
        <div>
          <div style={{
            fontWeight: '600',
            fontSize: '16px',
            color: '#333',
            marginBottom: '4px'
          }}>
            {review.locationName}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#666',
            marginBottom: '8px'
          }}>
            {formatDate(review.timestamp)}
          </div>
          <StarDisplay rating={review.overallSafety} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{
            padding: '4px 8px',
            backgroundColor: '#e3f2fd',
            borderRadius: '12px',
            fontSize: '12px',
            color: '#1976d2'
          }}>
            👍 {review.helpfulVotes || 0}
          </div>
          <button
            onClick={() => handleDeleteReview(review.id)}
            style={{
              padding: '4px 8px',
              backgroundColor: '#ffebee',
              color: '#c62828',
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Safety Ratings */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '8px',
        marginBottom: '12px',
        padding: '8px',
        backgroundColor: '#f9f9f9',
        borderRadius: '6px'
      }}>
        <div>
          <div style={{ fontSize: '11px', color: '#666' }}>Lighting</div>
          <StarDisplay rating={review.lightingQuality} />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#666' }}>Crowd</div>
          <StarDisplay rating={review.crowdLevels} />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#666' }}>Staff</div>
          <StarDisplay rating={review.staffHelpfulness} />
        </div>
        <div>
          <div style={{ fontSize: '11px', color: '#666' }}>Incidents</div>
          <span style={{
            fontSize: '12px',
            padding: '2px 4px',
            borderRadius: '4px',
            backgroundColor: review.harassmentIncident ? '#ffebee' : '#e8f5e8',
            color: review.harassmentIncident ? '#c62828' : '#2e7d32'
          }}>
            {review.harassmentIncident ? 'Yes' : 'No'}
          </span>
        </div>
      </div>

      {/* Review Content */}
      {review.comments && (
        <div style={{
          padding: '12px',
          backgroundColor: '#f5f5f5',
          borderRadius: '6px',
          fontSize: '14px',
          lineHeight: '1.4',
          marginBottom: '8px'
        }}>
          "{review.comments}"
        </div>
      )}

      {/* Photos */}
      {review.photos && review.photos.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '8px'
        }}>
          {review.photos.slice(0, 3).map((photo, index) => (
            <img
              key={index}
              src={photo.preview || photo.url}
              alt={`Review photo ${index + 1}`}
              style={{
                width: '60px',
                height: '60px',
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid #ddd'
              }}
            />
          ))}
          {review.photos.length > 3 && (
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '4px',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              color: '#666',
              border: '1px solid #ddd'
            }}>
              +{review.photos.length - 3}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !isAuthenticated) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          width: '90%',
          maxWidth: '800px',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 10
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
              Review History
            </h2>
            <VerificationBadges user={user} size="small" />
          </div>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Your travel safety reviews and contributions
          </p>
        </div>

        {/* Stats Summary */}
        <div style={{
          padding: '16px 24px',
          backgroundColor: '#f9f9f9',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '16px',
            textAlign: 'center'
          }}>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196F3' }}>
                {stats.totalReviews}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Total Reviews</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4CAF50' }}>
                {stats.averageRating?.toFixed(1) || '0.0'}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Avg Rating</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF9800' }}>
                {stats.helpfulVotes}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Helpful Votes</div>
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#9C27B0' }}>
                {stats.monthlyReviews}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>This Month</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          padding: '16px 24px',
          backgroundColor: '#f9f9f9',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <select
            value={filters.sortBy}
            onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
            <option value="helpful">Most Helpful</option>
          </select>

          <select
            value={filters.rating}
            onChange={(e) => setFilters(prev => ({ ...prev, rating: e.target.value }))}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4+ Stars</option>
            <option value="3">3+ Stars</option>
            <option value="2">2+ Stars</option>
            <option value="1">1+ Stars</option>
          </select>

          <input
            type="text"
            placeholder="Filter by location..."
            value={filters.location}
            onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '150px'
            }}
          />
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {error && (
            <div style={{
              backgroundColor: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              color: '#666'
            }}>
              Loading reviews...
            </div>
          ) : reviews.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>No reviews yet</div>
              <div style={{ fontSize: '14px' }}>
                Start sharing your travel safety experiences to help others!
              </div>
            </div>
          ) : (
            <div>
              {reviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewHistory;